import React, { useState, useRef, useEffect } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Cropper } from 'react-cropper';
import { Drawer, Button, Loader } from 'quantux-ui';
import { blue, gray, salmon } from '../../theme';
import ThumbnailsPanel from '../thumbnails-panel';
import { BASE_URL, equal } from '../../utils';
import { clone } from 'ramda';

const btnStyles = {
  maxHeight: '24px',
  maxWidth: '94px',
  minWidth: '94px',
  fontSize: '14px',
  padding: '0'
};

const CropperDrawer = (props) => {
  const [pages, setPages] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState('');
  const [cropperImageUrl, setCropperImageUrl] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [cropperDrawerWidth, setCropperDrawerWidth] = useState(window.innerWidth);
  const [cropperDrawerHeight, setCropperDrawerHeight] = useState(window.innerHeight);
  const [showThumbnailsPanel, setShowThumbnailsPanel] = useState(false);
  const [showCropLoader, setShowCropLoader] = useState(false);
  const [loaderWidth, setLoaderWidth] = useState('100%');
  const [loaderHeight, setLoaderHeight] = useState('100%');

  // Refs
  const cropperRef = useRef();

  // --- Effects
  const handleWindowResize = () => {
    setCropperDrawerWidth(window.innerWidth);
    setCropperDrawerHeight(window.innerHeight);
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (!equal(props.caseDocs, pages)) {
      setPages(props.caseDocs);
    }
  }, [props.caseDocs]);

  useEffect(() => {
    if (selectedDocument !== props.activeDocId || selectedDocument === '') {
      setSelectedDocument(props.activeDocId);
    }
  }, [props.activeDocId]);

  useEffect(() => {
    if (Array.isArray(pages) && pages.length > 0 && selectedDocument.length > 0) {
      const tmpIndex = pages.findIndex(el => el.id === selectedDocument);
      setCropperImageUrl(pages[tmpIndex].compositeImage);
      setCurrentPage(tmpIndex + 1);
    }
  }, [selectedDocument]);

  const styles = StyleSheet.create({
    drawerContainer: {
      position: 'relative',
      background: blue[900],
      color: blue[200],
      height: '100%',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0',
      background: blue[700]
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      opacity: showLoader ? '0.3' : '1',
      ':hover': {
        color: !showLoader ? salmon[500] : ''
      }
    },
    toolbar: {
      height: '40px',
      background: gray[500],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px'
    },
    toolbarLeft: {
      height: '40px',
      display: 'flex',
      alignItems: 'center'
    },
    toolbarRight: {
      height: '40px',
      display: 'flex',
      alignItems: 'center'
    },
    toolbarIcon: {
      fontSize: '22px',
      color: gray[300],
      fill: '#96C8FA',
      cursor: 'pointer',
      ':hover': {
        fill: blue[400],
        color: blue[400]
      }
    },
    drawerContentArea: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 'calc(100vh - 80px)',
      overflowY: 'hidden',
      color: gray[300],
      position: 'relative',
      zIndex: '1'
    },
    badge: {
      position: 'absolute',
      top: '18px',
      right: '0px',
      padding: '0 4px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: blue[500],
      borderRadius: '4px',
      color: 'white',
      cursor: 'pointer'
    },
    pageSelector: {
      display: 'flex',
      height: '30px',
      width: '120px',
      border: `1px solid ${gray[800]}`,
      borderRadius: '5px',
      marginLeft: '16px'
    },
    pageSelectorBtn: {
      fontSize: '12px',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: blue[800]
      // background: '#083050'
    },
    pageSelectorPageNumberDisplay: {
      width: '80x',
      background: 'black',
      color: gray[300],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: '1',
      height: '30px'
    },
    loaderContainer: {
      position: 'absolute',
      zIndex: '1',
      background: '#00A0FF80'
    }
  });

  const findActivePageIndex = () => pages.findIndex(el => el.id === selectedDocument);

  const handleOnThumbnailSelected = (id) => {
    setSelectedDocument(id);
  };

  // Back button click event handler
  const handleOnClickBackBtn = () => {
    props.onClose(pages);
  };

  // Crop button event handler
  const handleOnClickCrop = async () => {
    setShowCropLoader(true);
    const { cropper } = cropperRef.current;
    const croppedImageDataUrl = cropper.getCroppedCanvas().toDataURL('image/jpeg', 0.7);

    if (!props.isNewCase) {
      const formData = new FormData();
      // formData.append('compositeImage', croppedImageDataUrl);
      formData.append('txt', new Blob([croppedImageDataUrl], { type: 'application/text' }));
      formData.append('scheduleId', props.scheduleId);
      formData.append('docId', selectedDocument);
      formData.append('formType', props.formType);

      // Authorization req heraders
      const reqHeaders = new Headers();
      const token = localStorage.getItem('token');
      reqHeaders.append('Authorization', `Bearer ${token}`);

      const options = {
        method: 'POST',
        body: formData,
        redirect: 'follow',
        headers: reqHeaders
      };

      const updateResponse = await fetch(`${BASE_URL}/casesSchedule/updateCroppedCompositeImage`, options);
      const updateResult = await updateResponse.json();

      if (updateResult.success) {
        console.log(updateResult.msg);
        const idx = findActivePageIndex();
        const updatedPages = clone(pages);
        updatedPages[idx].compositeImage = croppedImageDataUrl;

        setShowCropLoader(false);
        setPages(updatedPages);
        setCropperImageUrl(croppedImageDataUrl);
      }
    } else {
      const idx = pages.findIndex((el) => {
        return el.id === selectedDocument;
      });
      const updatedPages = clone(pages);
      updatedPages[idx].compositeImage = croppedImageDataUrl;
      setShowCropLoader(false);
      setPages(updatedPages);
      setCropperImageUrl(croppedImageDataUrl);
    }
  };

  return (
    <Drawer
      placement='right'
      visible
      toggleDrawer={() => console.log('Not applicable')}
      zIndex="50000"
      showMask={false}
      style={{ border: 'none' }}
      width={window.innerWidth}
    >
      <div className={css(styles.drawerContainer)}>
        {/* Drawer Header */}
        <div className={css(styles.drawerHeader)}>
          <div className='fx fx__hv-center'>
            <div className='ml-2'>
              Document Name
            </div>
          </div>
          <i
            className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
            onClick={() => {
              props.onCloseDocWriter(pages);
            }}
          />
        </div>
        {/* ---------- Drawer Toolbar ---------- */}
        <div className={css(styles.toolbar)}>
          {/* Toolbar left section */}
          <div className={css(styles.toolbarLeft)}>
            {/* Thumbnails drawer icon */}
            <div
              style={{
                position: 'relative',
                paddingTop: '6px'
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="30"
                fill="none"
                className={css(styles.toolbarIcon)}
                onClick={() => {
                  setShowThumbnailsPanel(!showThumbnailsPanel);
                }}
              >
                <path
                  fillRule="evenodd"
                  d="M29.285 0H2.215C.99 0 0 1.258 0 2.813v24.375C0 28.741.99 30 2.215 30h27.07c1.224 0 2.215-1.258 2.215-2.813V2.813C31.5 1.258 30.51 0 29.285 0ZM12 27H3V3h9v24Zm3 0h13.5V3c0 .085-.01.142-.022.142H15.022C15.009 3.142 15 3.071 15 3v24Zm-4.5-16.5h-6v-6h6v6Zm-6 7.5h6v-6h-6v6Z"
                  clipRule="evenodd"
                />
              </svg>
              {/* Badge */}
              <div
                className={css(styles.badge)}
                onClick={() => {
                  console.log('Not implemented yet!');
                }}
              >
                {pages.length || 0}
              </div>
            </div>
            {/* Page Selector */}
            <div className={`ml-2 ${css(styles.pageSelector)}`}>
              {/* Go Back */}
              <div
                className={css(styles.pageSelectorBtn)}
                style={{
                  fontSize: '18px',
                  borderTopLeftRadius: '5px',
                  borderBottomLeftRadius: '5px',
                  color: '#5080B0'
                }}
                onClick={() => {
                  if (currentPage > 1) {
                    const tmpIndex = currentPage - 2;
                    setCropperImageUrl(pages[tmpIndex].compositeImage);
                    setCurrentPage(tmpIndex + 1);
                  }
                }}
              >
                <i className='fa fa-minus-circle' />
              </div>
              {/* Current Page */}
              <div className={css(styles.pageSelectorPageNumberDisplay)}>
                {currentPage}
              </div>
              {/* Go Forward */}
              <div
                className={css(styles.pageSelectorBtn)}
                style={{
                  color: '#5080B0',
                  fontSize: '18px',
                  borderTopRightRadius: '5px',
                  borderBottomRightRadius: '5px'
                }}
                onClick={() => {
                  if (currentPage < pages.length) {
                    const tmpIndex = currentPage;
                    setCropperImageUrl(pages[tmpIndex].compositeImage);
                    setCurrentPage(tmpIndex + 1);
                  }
                }}
              >
                <i className='fa fa-plus-circle' />
              </div>
            </div>
          </div>
          {/* Toolbar right section */}
          <div className={css(styles.toolbarRight)}>
            {/* Back Button */}
            <Button
              btnType='primary'
              isOutlineBtn
              style={btnStyles}
              className='fx fx__hv-center'
              onClick={handleOnClickBackBtn}
            >
              Back
            </Button>
            {/* Crop Button */}
            <Button
              btnType='primary'
              style={btnStyles}
              className='ml-2 fx fx__hv-center'
              onClick={handleOnClickCrop}
            >
              {showCropLoader ? 'Cropping' : 'Crop'}
            </Button>
          </div>
        </div>
        {/* -------------------- Drawer Container -------------------- */}
        <div className={css(styles.drawerContentArea)}>
          {/* ---------- Cropper Loader ---------- */}
          {/* {showCropLoader &&
            <div
              className={css(styles.loaderContainer)}
              style={{
                width: loaderWidth,
                height: loaderHeight
              }}
            >
              <Loader
                iconType="spinnerWithPulse"
                iconSize="5x"
                text="Cropping Image"
              />
            </div>
          } */}
          {/* ---------- Thumbnails Panel ---------- */}
          {showThumbnailsPanel &&
            <ThumbnailsPanel
              caseDocs={pages}
              activeDocId={selectedDocument}
              onThumbnailSelected={(id) => setSelectedDocument(id)}
              onPageSelected={handleOnThumbnailSelected}
            />
          }
          {/* -------------------- Cropper -------------------- */}
          <Cropper
            ref={cropperRef}
            src={cropperImageUrl}
            style={{
              height: `${cropperDrawerHeight - 200}px`,
              width: `${cropperDrawerWidth}px`
            }}
            checkCrossOrigin
            checkOrientation
            background={false}
            viewMode={1}
            guides
            minCropBoxWidth={50}
            minCropBoxHeight={50}
            autoCropArea={1}
            dragMode='none'
            zoomable={false}
            ready={() => {
              console.log('Cropper ready!');
              if (cropperRef.current && cropperRef.current.cropper) {
                const cropperBox = document.querySelector('.cropper-crop-box');
                setLoaderWidth(cropperBox.style.width);
                setLoaderHeight(cropperBox.style.height);
              }
            }}
            cropend={() => {
              console.log('Crop End');
            }}
          />
        </div>
      </div>
    </Drawer>
  );
};

CropperDrawer.defaultProps = {
  isNewCase: false
};
export default CropperDrawer;
